import { useState, useEffect } from "react";
import { useStore } from "../context/StoreContext";
import { createOrder, formatINR, razorpayCreateOrder, razorpayVerify, resolveMediaUrl, lookupPincode, validateCoupon, fetchAutoApplyCoupon } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Shield, Truck, BadgeCheck, Check, Loader2, Tag, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { INDIAN_STATES, FREE_SHIPPING_THRESHOLD, STANDARD_SHIPPING, PREPAY_DISCOUNT } from "../lib/constants";

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const initialAddr = {
  full_name: "", phone: "", email: "",
  line1: "", line2: "",
  city: "", state: "", pincode: "",
  country: "India",
};

export default function CheckoutPage() {
  const { cart, subtotal, clearCart, bundleDiscount } = useStore();
  const { user } = useAuth();
  const [addr, setAddr] = useState(initialAddr);
  const [method, setMethod] = useState("razorpay");
  const [submitting, setSubmitting] = useState(false);
  const [pinLookup, setPinLookup] = useState({ loading: false, options: [], error: "" });
  const [coupon, setCoupon] = useState({ input: "", applied: null, error: "", busy: false, isAuto: false });
  const navigate = useNavigate();

  const bundleSavings = bundleDiscount?.total || 0;
  const effectiveSubtotal = Math.max(0, subtotal - bundleSavings);
  const shipping = effectiveSubtotal >= FREE_SHIPPING_THRESHOLD || effectiveSubtotal === 0 ? 0 : STANDARD_SHIPPING;
  const remainingForFree = Math.max(0, FREE_SHIPPING_THRESHOLD - effectiveSubtotal);
  const discount = coupon.applied?.discount || 0;
  const prepayOff = method === "razorpay" ? PREPAY_DISCOUNT : 0;
  const total = Math.max(0, effectiveSubtotal - discount - prepayOff + shipping);

  // Auto-apply LOYAL10 for repeat customers (only on first paint after login)
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (coupon.applied || coupon.busy) return;
    fetchAutoApplyCoupon().then((d) => {
      if (!d?.coupon) return;
      validateCoupon(d.coupon.code, subtotal).then((res) => {
        setCoupon({ input: res.code, applied: res, error: "", busy: false, isAuto: true });
        toast.success("Welcome back! We've applied your loyalty discount 🎉");
      }).catch(() => {});
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Re-validate applied coupon when subtotal changes (e.g. cart updates)
  useEffect(() => {
    if (!coupon.applied) return;
    validateCoupon(coupon.applied.code, subtotal).then((res) => {
      setCoupon((c) => ({ ...c, applied: res, error: "" }));
    }).catch((ex) => {
      setCoupon((c) => ({ ...c, applied: null, error: ex?.response?.data?.detail || "Coupon no longer valid", isAuto: false }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const applyCouponManually = async () => {
    const code = (coupon.input || "").trim().toUpperCase();
    if (!code) return;
    setCoupon((c) => ({ ...c, busy: true, error: "" }));
    try {
      const res = await validateCoupon(code, subtotal);
      setCoupon({ input: code, applied: res, error: "", busy: false, isAuto: false });
      toast.success(`${code} applied · ₹${Math.round(res.discount)} off`);
    } catch (ex) {
      setCoupon((c) => ({ ...c, busy: false, error: ex?.response?.data?.detail || "Invalid coupon" }));
    }
  };

  const removeCoupon = () => setCoupon({ input: "", applied: null, error: "", busy: false, isAuto: false });

  const required = ["full_name", "phone", "email", "line1", "city", "state", "pincode"];
  const isValid = required.every((k) => addr[k]?.trim()) && cart.length > 0;

  const handleChange = (k, v) => setAddr((a) => ({ ...a, [k]: v }));

  const handlePincodeChange = async (v) => {
    const cleaned = v.replace(/\D/g, "").slice(0, 6);
    handleChange("pincode", cleaned);
    setPinLookup({ loading: false, options: [], error: "" });
    if (cleaned.length === 6) {
      setPinLookup({ loading: true, options: [], error: "" });
      try {
        const data = await lookupPincode(cleaned);
        const first = Array.isArray(data) ? data[0] : null;
        if (!first || first.Status !== "Success" || !Array.isArray(first.PostOffice) || !first.PostOffice.length) {
          setPinLookup({ loading: false, options: [], error: "Pincode not found" });
          return;
        }
        const offices = first.PostOffice;
        // Dedupe by (Name + District)
        const seen = new Set();
        const options = offices.filter((o) => {
          const k = `${o.Name}|${o.District}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setPinLookup({ loading: false, options, error: "" });
        if (options.length === 1) {
          const o = options[0];
          setAddr((a) => ({ ...a, city: o.District, state: o.State }));
        } else {
          // Pre-set state so dropdown aligns
          const firstState = options[0].State;
          setAddr((a) => ({ ...a, state: firstState }));
        }
      } catch {
        setPinLookup({ loading: false, options: [], error: "Couldn't reach pincode service" });
      }
    }
  };

  const pickArea = (o) => {
    setAddr((a) => ({ ...a, city: o.District, state: o.State }));
    setPinLookup((p) => ({ ...p, options: [] }));
  };

  const placeOrder = async () => {
    if (!isValid) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      if (method === "razorpay") {
        const ok = await loadRazorpayScript();
        if (!ok) {
          toast.error("Could not load payment gateway");
          setSubmitting(false);
          return;
        }
        const rOrder = await razorpayCreateOrder(total);
        await new Promise((resolve, reject) => {
          const rzp = new window.Razorpay({
            key: rOrder.key_id || RAZORPAY_KEY_ID,
            amount: rOrder.amount,
            currency: rOrder.currency || "INR",
            name: "Her Box",
            description: "Order payment",
            order_id: rOrder.id,
            prefill: { name: addr.full_name, email: addr.email, contact: addr.phone },
            theme: { color: "#F5D565" },
            handler: async (resp) => {
              try {
                await razorpayVerify({
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                });
                const order = await createOrder({
                  items: cart.map((c) => ({
                    product_id: c.product_id, name: c.name, price: c.price, image: c.image, quantity: c.quantity,
                  })),
                  address: addr,
                  payment_method: "razorpay",
                  subtotal, shipping, total,
                  bundle_discount: bundleSavings,
                  coupon_code: coupon.applied?.code,
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                });
                toast.success("Payment received");
                clearCart();
                navigate(`/order-confirmation/${order.order_number}`);
                resolve();
              } catch (e) {
                toast.error("Payment verification failed");
                reject(e);
              }
            },
            modal: {
              ondismiss: () => {
                toast("Payment cancelled");
                setSubmitting(false);
                resolve();
              },
            },
          });
          rzp.on("payment.failed", () => {
            toast.error("Payment failed. Please try again.");
            setSubmitting(false);
            resolve();
          });
          rzp.open();
        });
        return;
      }

      // COD path
      const order = await createOrder({
        items: cart.map((c) => ({
          product_id: c.product_id, name: c.name, price: c.price, image: c.image, quantity: c.quantity,
        })),
        address: addr,
        payment_method: "cod",
        subtotal, shipping, total,
        bundle_discount: bundleSavings,
        coupon_code: coupon.applied?.code,
      });
      clearCart();
      navigate(`/order-confirmation/${order.order_number}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <main className="bg-[#0A0A0A] min-h-screen pt-[120px] px-6">
        <div className="max-w-3xl mx-auto py-24 text-center">
          <h1 className="font-serif-display text-5xl">Your box is empty.</h1>
          <button onClick={() => navigate("/shop")} className="btn-gold mt-8">Shop Now</button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="bg-[#0A0A0A] min-h-screen pt-[100px] pb-24">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-3">
          ✦ Checkout
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl tracking-tight">
          Almost <em className="not-italic gold-text">yours.</em>
        </h1>

        {/* Free delivery banner */}
        <div
          data-testid="free-delivery-banner"
          className="mt-6 px-4 py-3 border font-ui"
          style={{
            background: shipping === 0 ? "rgba(46,140,82,0.12)" : "rgba(245,213,101,0.06)",
            borderColor: shipping === 0 ? "#3E7A4A" : "#F5D56540",
            fontSize: 13, letterSpacing: "0.05em",
            color: shipping === 0 ? "#90D7A3" : "rgba(255,255,255,0.85)",
          }}
        >
          {shipping === 0
            ? "🎉 You've unlocked FREE delivery!"
            : `Add ${formatINR(remainingForFree)} more to get FREE delivery.`}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-12 mt-10">
          {/* Form */}
          <div>
            <h2 className="font-serif-display text-2xl mb-4">Shipping address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Full Name *" v={addr.full_name} on={(v) => handleChange("full_name", v)} testid="checkout-fullname" />
              <Input label="Phone *" v={addr.phone} on={(v) => handleChange("phone", v)} testid="checkout-phone" />
              <Input label="Email *" v={addr.email} on={(v) => handleChange("email", v)} testid="checkout-email" full />
              <Input label="Address Line 1 *" v={addr.line1} on={(v) => handleChange("line1", v)} testid="checkout-line1" full />
              <Input label="Address Line 2" v={addr.line2} on={(v) => handleChange("line2", v)} testid="checkout-line2" full />

              {/* Country (locked) */}
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Country *</span>
                <input
                  value="India"
                  readOnly
                  data-testid="checkout-country"
                  className="mt-2 w-full bg-[#141414] border-b border-[#262626] py-2.5 text-sm text-[#A3A3A3] outline-none cursor-not-allowed px-2"
                />
              </label>

              {/* Pincode with lookup */}
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] flex items-center gap-2">
                  Pincode *
                  {pinLookup.loading && <Loader2 size={12} className="animate-spin text-[#F5D565]" />}
                </span>
                <input
                  data-testid="checkout-pincode"
                  value={addr.pincode}
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(e) => handlePincodeChange(e.target.value)}
                  className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 text-sm focus:border-[#D4AF37] outline-none"
                />
                {pinLookup.error && (
                  <span className="block mt-1 text-[11px] text-red-400">{pinLookup.error}</span>
                )}
              </label>

              {/* State dropdown */}
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">State *</span>
                <select
                  data-testid="checkout-state"
                  value={addr.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 text-sm focus:border-[#D4AF37] outline-none appearance-none"
                >
                  <option value="" className="bg-[#0A0A0A]">Select state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s} className="bg-[#0A0A0A]">{s}</option>
                  ))}
                </select>
              </label>

              <Input label="City / District *" v={addr.city} on={(v) => handleChange("city", v)} testid="checkout-city" full />
            </div>

            {/* Multi-area picker */}
            {pinLookup.options.length > 1 && (
              <div
                data-testid="checkout-area-picker"
                className="mt-4 border border-[#262626] p-4"
              >
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#F5D565] mb-3">Select your area</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {pinLookup.options.slice(0, 20).map((o, idx) => {
                    const active = addr.city === o.District;
                    return (
                      <button
                        type="button"
                        key={`${o.Name}-${idx}`}
                        onClick={() => pickArea(o)}
                        data-testid={`area-option-${idx}`}
                        className="flex items-start justify-between gap-2 text-left px-3 py-2.5 border transition-all"
                        style={{
                          borderColor: active ? "#F5D565" : "#262626",
                          background: active ? "rgba(245,213,101,0.08)" : "transparent",
                        }}
                      >
                        <div>
                          <div className="font-ui text-sm" style={{ color: "#FFFFFF" }}>{o.Name}</div>
                          <div className="font-body text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.55)" }}>
                            {o.District}, {o.State}
                          </div>
                        </div>
                        {active && <Check size={14} className="text-[#F5D565] flex-shrink-0 mt-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <h2 className="font-serif-display text-2xl mt-10 mb-4">Payment</h2>
            <div className="space-y-3">
              <PaymentOption
                active={method === "razorpay"}
                onClick={() => setMethod("razorpay")}
                title="Razorpay · Cards · UPI · Wallets"
                sub={`Save ${formatINR(PREPAY_DISCOUNT)} when you prepay online — secure checkout by Razorpay`}
                testid="payment-razorpay"
                badge={`Save ${formatINR(PREPAY_DISCOUNT)}`}
              />
              <PaymentOption
                active={method === "cod"}
                onClick={() => setMethod("cod")}
                title="Cash on Delivery"
                sub="Pay when it arrives. Available across India."
                testid="payment-cod"
              />
            </div>
          </div>

          {/* Summary */}
          <aside className="bg-[#141414] border border-[#262626] p-6 md:p-8 h-fit lg:sticky lg:top-24">
            <h2 className="font-serif-display text-2xl mb-5">Your Box</h2>
            <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2">
              {cart.map((it) => (
                <div key={it.product_id} className="flex gap-3">
                  <img src={resolveMediaUrl(it.image)} alt="" className="w-16 h-20 object-cover bg-[#0A0A0A]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-sm truncate">{it.name}</div>
                    <div className="text-xs text-[#A3A3A3] mt-1">x {it.quantity}</div>
                  </div>
                  <div className="text-sm">{formatINR(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#262626] my-5" />

            {/* Coupon */}
            <div data-testid="coupon-block" className="mb-4">
              {coupon.applied ? (
                <div className="border border-[#F5D565] bg-[#F5D565]/[0.06] px-3 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Tag size={14} className="text-[#F5D565] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-ui text-[12px] truncate" style={{ color: "#F5D565", letterSpacing: "0.1em" }}>
                        {coupon.applied.code} applied{coupon.isAuto ? " · loyalty" : ""}
                      </div>
                      <div className="font-body text-[11px] truncate" style={{ color: "rgba(255,255,255,0.7)" }}>
                        −{formatINR(coupon.applied.discount)} off
                      </div>
                    </div>
                  </div>
                  <button onClick={removeCoupon} data-testid="coupon-remove" className="text-[#A3A3A3] hover:text-[#F5D565] flex-shrink-0">
                    <XIcon size={14} />
                  </button>
                </div>
              ) : (
                <details className="group">
                  <summary className="font-ui inline-flex items-center gap-1.5 cursor-pointer hover:text-[#F5D565] transition-colors"
                           style={{ fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}
                           data-testid="coupon-toggle">
                    <Tag size={12} strokeWidth={1.5} /> Have a coupon code?
                  </summary>
                  <div className="mt-3 flex gap-2">
                    <input
                      value={coupon.input}
                      onChange={(e) => setCoupon((c) => ({ ...c, input: e.target.value }))}
                      placeholder="ENTER CODE"
                      data-testid="coupon-input"
                      className="flex-1 bg-transparent border border-[#262626] focus:border-[#F5D565] outline-none px-3 py-2 text-sm uppercase tracking-[0.15em]"
                      style={{ color: "#FFFFFF" }}
                    />
                    <button
                      onClick={applyCouponManually}
                      disabled={coupon.busy || !coupon.input}
                      data-testid="coupon-apply"
                      className="px-4 py-2 border border-[#F5D565] text-[#F5D565] font-ui hover:bg-[#F5D565] hover:text-[#0A0A0A] transition-all disabled:opacity-50"
                      style={{ fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase" }}
                    >
                      {coupon.busy ? "…" : "Apply"}
                    </button>
                  </div>
                  {coupon.error && (
                    <p data-testid="coupon-error" className="mt-2 text-[11px] text-red-400">{coupon.error}</p>
                  )}
                </details>
              )}
            </div>

            <Row k="Subtotal" v={formatINR(subtotal)} />
            {bundleSavings > 0 && (
              <Row k="Bundle Offer" v={`−${formatINR(bundleSavings)}`} testid="row-bundle-discount" accent />
            )}
            {discount > 0 && (
              <Row k="Discount" v={`−${formatINR(discount)}`} testid="row-discount" accent />
            )}
            {prepayOff > 0 && (
              <Row k="Prepay & Save" v={`−${formatINR(prepayOff)}`} testid="row-prepay-discount" accent />
            )}
            <Row k="Shipping" v={shipping === 0 ? "Free" : formatINR(shipping)} />
            <div className="border-t border-[#262626] my-4" />
            <Row k="Total" v={formatINR(total)} big />

            <div
              data-testid="delivery-timeline"
              className="mt-4 px-3 py-2.5 border border-[#262626] font-ui"
              style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#F5D565" }}
            >
              ✦ Delivery in 5-7 working days
            </div>

            <button
              data-testid="place-order-btn"
              disabled={!isValid || submitting}
              onClick={placeOrder}
              className="btn-gold w-full mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Placing…" : `Place Order · ${formatINR(total)}`}
            </button>

            <p
              data-testid="spam-folder-note"
              className="mt-3 text-[11px] leading-relaxed text-[#A3A3A3]"
            >
              If order confirmation is not visible in your inbox, please check your spam folder.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-3 text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">
              <Trust icon={<Shield size={14} strokeWidth={1.5} />} t="Secure" />
              <Trust icon={<Truck size={14} strokeWidth={1.5} />} t="Fast Ship" />
              <Trust icon={<BadgeCheck size={14} strokeWidth={1.5} />} t="Authentic" />
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

const Input = ({ label, v, on, testid, full = false }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{label}</span>
    <input
      data-testid={testid}
      value={v}
      onChange={(e) => on(e.target.value)}
      className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 text-sm focus:border-[#D4AF37] outline-none"
    />
  </label>
);

const Row = ({ k, v, big = false, accent = false, testid }) => (
  <div className="flex items-center justify-between py-1.5" data-testid={testid}>
    <span className={`text-xs tracking-[0.2em] uppercase ${big ? "text-[#F5F5F5]" : "text-[#A3A3A3]"}`}>{k}</span>
    <span className={big ? "font-serif-display text-2xl" : `text-sm ${accent ? "text-[#F5D565]" : ""}`}>{v}</span>
  </div>
);

const PaymentOption = ({ active, onClick, title, sub, testid, badge }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`w-full text-left p-5 border transition-all ${
      active ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#262626] hover:border-[#D4AF37]/50"
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="font-serif-display text-lg">{title}</span>
      {badge && (
        <span className="text-[9px] tracking-[0.3em] uppercase border border-[#D4AF37] text-[#D4AF37] px-2 py-1">
          {badge}
        </span>
      )}
    </div>
    <p className="text-xs text-[#A3A3A3] mt-1">{sub}</p>
  </button>
);

const Trust = ({ icon, t }) => (
  <div className="flex flex-col items-center gap-2 text-center">
    <span className="text-[#D4AF37]">{icon}</span>
    <span>{t}</span>
  </div>
);
